import { getLocale, t } from '../i18n';

/*
 * Locale-aware formatting. Formatters are cached per locale; components re-render on a language change
 * (they subscribe via useI18n), so every call picks up the current locale.
 */
const cache = new Map<string, Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.NumberFormat>();
function fmt<T extends Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.NumberFormat>(key: string, make: (locale: string) => T): T {
  const k = `${getLocale()}|${key}`;
  let f = cache.get(k) as T | undefined;
  if (!f) {
    f = make(getLocale());
    cache.set(k, f);
  }
  return f;
}
const dateTime = () => fmt('dt', (l) => new Intl.DateTimeFormat(l, { dateStyle: 'medium', timeStyle: 'short' }));
const relative = () => fmt('rel', (l) => new Intl.RelativeTimeFormat(l, { numeric: 'auto' }));
const shortDate = () => fmt('short', (l) => new Intl.DateTimeFormat(l, { month: 'short', day: 'numeric', timeZone: 'UTC' }));
const decimal = (digits: number) => fmt(`num${digits}`, (l) => new Intl.NumberFormat(l, { maximumFractionDigits: digits }));
const percent = () => fmt('pct', (l) => new Intl.NumberFormat(l, { style: 'percent', maximumFractionDigits: 1 }));
const oneDecimal = () => fmt('fix1', (l) => new Intl.NumberFormat(l, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

/** English label for an enum-like value (e.g. "in_progress" → "In progress"), then translated. */
export const humanize = (value: string): string => {
  if (value === 'qa_engineer') return t('QA engineer');
  const s = value.replace(/_/g, ' ');
  return t(s.charAt(0).toUpperCase() + s.slice(1));
};

export const formatDateTime = (iso: string): string => dateTime().format(new Date(iso));
export const formatNumber = (n: number, digits = 1): string => decimal(digits).format(n);
/** 71.4 → "71.4%" (en) / "71,4 %" (es). Input is already a percentage, not a fraction. */
export const formatPercent = (n: number): string => percent().format(n / 100);

export function formatRelative(iso: string, now = Date.now()): string {
  const diffMin = Math.round((new Date(iso).getTime() - now) / 60_000);
  if (Math.abs(diffMin) < 60) return relative().format(diffMin, 'minute');
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 48) return relative().format(diffH, 'hour');
  return relative().format(Math.round(diffH / 24), 'day');
}

export function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'string') return /^[a-z_]+$/.test(value) ? humanize(value) : value;
  return JSON.stringify(value);
}

/** Formats a report cell by column type (UTC dates: reports are computed in UTC). */
export function formatCellValue(value: string | number | null | undefined, type: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    if (type === 'percent') return formatPercent(value);
    if (type === 'hours') return formatHours(value);
    if (type === 'rating') return formatNumber(value, 2);
    return formatNumber(value, 2);
  }
  if (type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return shortDate().format(new Date(`${value}T00:00:00Z`));
  return /^[a-z_]+$/.test(value) ? humanize(value) : t(value);
}

export function formatHours(h: number): string {
  if (h < 48) return t('{n} h', { n: formatNumber(h) });
  return t('{n} d', { n: formatNumber(h / 24) });
}

export const formatShortDate = (iso: string): string => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? shortDate().format(new Date(`${iso}T00:00:00Z`)) : iso);

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${oneDecimal().format(bytes / 1024)} KB`;
  return `${oneDecimal().format(bytes / 1024 ** 2)} MB`;
}
